<template>
  <div class="subscribe-page">
    <div class="subscribe-card">
      <!-- Two-step wizard: Account → Billing. Drawing the market on
           /my-market is a pre-wizard activity and isn't counted as a
           numbered step — by the time the user is here they've already
           drawn their market and signed up. -->
      <div class="signup-stepper">
        <div class="signup-step signup-step-done">
          <div class="signup-step-circle">&#10003;</div>
          <div class="signup-step-label">Account</div>
        </div>
        <div class="signup-step-line signup-step-line-done"></div>
        <div class="signup-step signup-step-active">
          <div class="signup-step-circle">2</div>
          <div class="signup-step-label">Billing</div>
        </div>
      </div>

      <!-- Pre-checkout subscribe page. Shown until the user clicks Continue
           and we hand off to Stripe. Required by California §17602 / FTC
           "clear and conspicuous" — the user must see and acknowledge
           recurring billing terms before any authorization is captured. -->
      <template v-if="!checkout_started && !error_message">
        <header class="subscribe-header">
          <p class="subscribe-eyebrow">Step 2 of 2</p>
          <h1 class="subscribe-title">Subscribe to Kiey Market Exclusivity</h1>
        </header>

        <!-- ──────────────────────────────────────────────────────────
             Section 1 — Market Summary
             Pulls from state.Market_billing.my_market (zip_codes) +
             billing_info (population/price). If neither is loaded yet,
             we show a low-key placeholder so the page never blocks on
             a slow Firestore read.
             ────────────────────────────────────────────────────────── -->
        <section class="subscribe-section">
          <h2 class="section-title">Your Market</h2>
          <div class="market-card">
            <div class="market-card-row">
              <div class="market-card-cell">
                <div class="market-card-label">Market</div>
                <div class="market-card-value">
                  <span v-if="market_label">{{ market_label }}</span>
                  <span v-else class="muted">Your selected territory</span>
                </div>
              </div>
              <div class="market-card-cell">
                <div class="market-card-label">ZIPs</div>
                <div class="market-card-value">
                  <span v-if="zip_count">{{ zip_count }}</span>
                  <span v-else class="muted">&mdash;</span>
                </div>
              </div>
              <div class="market-card-cell">
                <div class="market-card-label">Total Population</div>
                <div class="market-card-value">
                  <span v-if="market_population">{{ market_population.toLocaleString('en-US') }}</span>
                  <span v-else class="muted">Loading&hellip;</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ──────────────────────────────────────────────────────────
             Section 2 — Pricing Breakdown
             Displays the formula we use to compute the monthly fee
             explicitly so the user can verify the number we're about
             to charge them. Override prices (super-admin-set) get a
             distinct callout and skip the calculation row.
             ────────────────────────────────────────────────────────── -->
        <section class="subscribe-section">
          <h2 class="section-title">Pricing Breakdown</h2>
          <p v-if="!has_override" class="section-caption">
            Pricing scales with your market &mdash; $1 per 1,000 residents, with a $295 monthly minimum.
          </p>
          <p v-else class="section-caption">
            A custom monthly price was set for your account by Kiey. The standard population-based pricing does not apply.
          </p>
          <div class="pricing-card">
            <template v-if="has_override">
              <div class="pricing-row pricing-row-override">
                <div class="pricing-label">Override price</div>
                <div class="pricing-value">${{ formatted_price }}.00</div>
              </div>
            </template>
            <template v-else>
              <div class="pricing-row">
                <div class="pricing-label">Your population</div>
                <div class="pricing-value">
                  <span v-if="market_population">{{ market_population.toLocaleString('en-US') }}</span>
                  <span v-else class="muted">&mdash;</span>
                </div>
              </div>
              <div class="pricing-row pricing-row-emphasis">
                <div class="pricing-label">Your monthly fee</div>
                <div class="pricing-value">
                  <span v-if="formatted_price">${{ formatted_price }}.00</span>
                  <span v-else class="muted">Calculating&hellip;</span>
                </div>
              </div>
            </template>
            <div class="pricing-row pricing-row-tax">
              <div class="pricing-label">Sales tax</div>
              <div class="pricing-value pricing-tax-note">Calculated at checkout</div>
            </div>
            <div class="pricing-divider"></div>
            <div class="pricing-row pricing-row-total">
              <div class="pricing-label">Estimated total</div>
              <div class="pricing-value">
                <span v-if="formatted_price">${{ formatted_price }}.00 + tax / month</span>
                <span v-else class="muted">&mdash;</span>
              </div>
            </div>
          </div>
        </section>

        <!-- "What You Get" intentionally lives on the public
             /my-market quote tool now, not here. By the time the user
             reaches /sign_up/billing they've already mentally committed
             — selling them again here is wasted real estate. The right
             moment for value framing is at price discovery (next to the
             calculated $/month on /my-market). See market_selection.vue
             > .my-market-includes. -->

        <!-- ──────────────────────────────────────────────────────────
             Section 4 — Billing Terms (mandatory acceptance)
             Scrollable summary box + required checkbox. The Continue
             button below remains disabled until the box is checked.
             Server-side re-asserts acceptance in create_checkout_session
             so a determined user can't bypass it via DevTools.
             ────────────────────────────────────────────────────────── -->
        <section class="subscribe-section">
          <h2 class="section-title">Billing Terms</h2>
          <div
            ref="terms_scrollbox"
            class="terms-scrollbox"
            tabindex="0"
            aria-label="Kiey Billing Terms — scroll to read"
            @scroll="onTermsScroll"
          >
            <h3 class="terms-heading">Kiey Billing Terms <span class="terms-version">v{{ billing_terms_version }}</span></h3>

            <ul class="terms-list">
              <li>
                <strong>Auto-renewal.</strong> Your subscription renews automatically on the same day each month
                until you cancel. The payment method you enter at checkout is charged the monthly amount
                displayed above (plus applicable tax) on a recurring basis.
              </li>
              <li>
                <strong>Cancellation.</strong> You can cancel anytime through your account settings.
                A 30-day notice period applies. During the notice period your subscription stays
                active and your market territory is preserved. Cancellations are processed through
                Kiey or the Stripe Customer Portal.
              </li>
              <li>
                <strong>Prorated refund.</strong> Once the 30-day notice period ends, Kiey issues a prorated
                refund for any unused days within the final billing cycle.
              </li>
              <li>
                <strong>Pricing changes.</strong> Kiey may change subscription pricing with at least 30 days&rsquo;
                advance notice. Population recalculation within the existing per-1,000 formula does
                not require separate notice — your market price tracks your population at each
                renewal.
              </li>
              <li>
                <strong>Failed payments.</strong> If a recurring charge fails, Kiey provides a 7-day grace
                period and up to 3 automatic retry attempts. After the grace period, the subscription
                is suspended and your market territory returns to the public waitlist.
              </li>
              <li>
                <strong>Tax.</strong> Applicable U.S. state and local sales tax and Canadian GST/HST/PST/QST are
                calculated automatically at checkout and on every renewal invoice based on your
                billing address.
              </li>
              <li>
                <strong>Receipts.</strong> Receipts are available in your account at admin.kiey.com under
                Account &rarr; Billing, and are also emailed by Stripe after every successful charge.
              </li>
              <li>
                <strong>Payment processor.</strong> All charges are processed by Stripe. After clicking Continue
                you&rsquo;ll be redirected to Stripe&rsquo;s secure, hosted checkout page. Kiey never sees or
                stores your full card number.
              </li>
              <li>
                <strong>Currency.</strong> Charges are billed in U.S. Dollars (USD), except where Kiey expressly
                invoices a Canadian customer in Canadian Dollars (CAD).
              </li>
              <li>
                <strong>Real Estate Team responsibilities.</strong> You are responsible for vetting the Vendors
                and Agents you invite to your market, and for ensuring their conduct meets your
                professional standards. See Section 12A of the Terms of Service for the full duty
                of care.
              </li>
            </ul>
            <p class="terms-footer">
              These billing terms are part of, and subject to, the full Kiey Terms of Service below.
              In case of conflict, the Terms of Service control.
            </p>

            <hr class="terms-divider" />

            <h3 class="terms-heading">Kiey Terms of Service <span class="terms-version">v{{ billing_terms_version }}</span></h3>
            <div v-if="full_tos_loading" class="muted">Loading the full Terms of Service&hellip;</div>
            <div v-else-if="full_tos_error" class="muted">{{ full_tos_error }}</div>
            <div
              v-else
              class="terms-fulltos"
              v-html="full_tos_html"
            ></div>

            <!-- End-of-document marker. The user crossing this boundary
                 (or anywhere within ~12px of it) flips terms_scrolled
                 to true, which enables the acceptance checkbox below. -->
            <div ref="terms_end_marker" class="terms-end-marker">
              <span aria-hidden="true">— End of Terms —</span>
            </div>
          </div>

          <!-- Scroll-to-bottom hint — visible only until the user
               actually reaches the end of the scrollbox. After that,
               it's replaced by the active acceptance checkbox. -->
          <p v-if="!terms_scrolled" class="terms-scroll-hint">
            Please scroll to the end of the Terms above to enable acceptance.
          </p>

          <label class="terms-consent" :class="{ 'terms-consent-disabled': !terms_scrolled }">
            <input
              type="checkbox"
              v-model="terms_accepted"
              :disabled="!terms_scrolled"
              :aria-disabled="!terms_scrolled ? 'true' : 'false'"
            />
            <span>
              I have read and agree to the Kiey Billing Terms above and authorize
              recurring monthly charges to the payment method I provide at checkout.
            </span>
          </label>
        </section>

        <!-- ──────────────────────────────────────────────────────────
             Section 4b — Coupon code (optional)
             Collapsible "Have a coupon code?" link → text input + Apply.
             We don't pre-validate via API — Stripe Checkout is the only
             authoritative validator. The optimistic local message just
             confirms we'll forward the code to checkout. If Stripe
             rejects the code, the create_checkout_session backend
             surfaces a clean 400 / INVALID_COUPON which lands in the
             error_message branch below.
             ────────────────────────────────────────────────────────── -->
        <section class="subscribe-section coupon-section">
          <button
            v-if="!coupon_open"
            type="button"
            class="coupon-toggle"
            @click="coupon_open = true"
            aria-expanded="false"
          >
            Have a coupon code?
          </button>
          <div v-else class="coupon-panel" aria-expanded="true">
            <label class="coupon-label" for="coupon-code-input">Coupon code</label>
            <div class="coupon-row">
              <input
                id="coupon-code-input"
                type="text"
                class="coupon-input"
                v-model.trim="coupon_input"
                :disabled="coupon_applied"
                placeholder="e.g. WELCOME50"
                autocomplete="off"
                spellcheck="false"
                @keyup.enter="applyCoupon"
              />
              <button
                v-if="!coupon_applied"
                type="button"
                class="coupon-btn"
                :disabled="!coupon_input"
                @click="applyCoupon"
              >
                Apply
              </button>
              <button
                v-else
                type="button"
                class="coupon-btn coupon-btn-clear"
                @click="clearCoupon"
              >
                Remove
              </button>
            </div>
            <p v-if="coupon_applied" class="coupon-note coupon-note-ok">
              Applied — your total will reflect the discount at checkout.
            </p>
            <p v-else class="coupon-note">
              Enter your code and we&rsquo;ll apply it on the secure Stripe checkout page.
            </p>
          </div>
        </section>

        <!-- ──────────────────────────────────────────────────────────
             Section 4c — Free Tier Partner Agreement (conditional)
             Only rendered when an applied coupon code's uppercase form
             contains "FREE" — that's the convention for free-tier
             coupons (e.g. KIEY_FREE_TEST, KIEY_FREE_PARTNER_2026,
             FREEMONTH). Re-uses the same scrollbox + required checkbox
             pattern as Billing Terms above. The backend records the
             accepted version on the tenant + user docs alongside the
             billing-terms acceptance.
             ────────────────────────────────────────────────────────── -->
        <section v-if="show_free_tier_terms" class="subscribe-section">
          <h2 class="section-title">Free Tier Partner Agreement</h2>
          <div class="terms-scrollbox terms-scrollbox-full" aria-label="Free Tier Partner Agreement">
            <div v-if="free_tier_terms_loading" class="muted">Loading the Free Tier Partner Agreement&hellip;</div>
            <div v-else-if="free_tier_terms_error" class="muted">
              {{ free_tier_terms_error }}
            </div>
            <article
              v-else
              class="free-terms-body"
              v-html="free_tier_terms_html"
            ></article>
          </div>

          <label class="terms-consent">
            <input type="checkbox" v-model="free_tier_terms_accepted" />
            <span>
              I have read and agree to the Kiey Free Tier Partner Agreement
              and the spirit of the partnership it describes.
            </span>
          </label>
        </section>

        <!-- ──────────────────────────────────────────────────────────
             Section 5 — Continue / submit
             Button is disabled until the acceptance checkbox is on. The
             Stripe redirect happens inside startCheckout(); we keep the
             user on the spinner state until window.location takes over.
             ────────────────────────────────────────────────────────── -->
        <div class="subscribe-actions">
          <button
            type="button"
            class="subscribe-btn subscribe-btn-primary"
            :disabled="continue_disabled"
            @click="confirmAndContinue"
          >
            {{ retrying ? 'Opening checkout&hellip;' : 'Continue to Secure Checkout' }}
          </button>
        </div>

        <!-- ──────────────────────────────────────────────────────────
             Section 6 — FAQ (collapsible)
             Native <details>/<summary> for free keyboard + screen-reader
             support; no JS required. Each item explains one of the more
             nuanced terms above in plain English.
             ────────────────────────────────────────────────────────── -->
        <section class="subscribe-section faq-section">
          <h2 class="section-title">Frequently Asked Questions</h2>
          <details class="faq-item">
            <summary>What if I cancel?</summary>
            <p>
              You can cancel anytime. A 30-day notice period applies — during those 30 days your
              subscription stays active and your market territory is preserved. After the notice
              ends, you get a prorated refund for any unused days in the final billing cycle.
            </p>
          </details>
          <details class="faq-item">
            <summary>What if my market&rsquo;s population changes?</summary>
            <p>
              Your monthly fee is recalculated at the start of each billing cycle using the same
              formula: $295 minimum for any market up to 295,000 residents, then $1 per 1,000
              residents above that. So 300,000 residents bills at $300/mo, 400,000 at $400/mo,
              and 1,000,000 at $1,000/mo. No separate notice is required for routine population
              recalculations.
            </p>
          </details>
          <details class="faq-item">
            <summary>Can I add more markets later?</summary>
            <p>
              Yes — you can expand your market territory or add adjacent ZIPs by contacting
              <a href="mailto:support@kiey.com">support@kiey.com</a>. Pricing adjusts automatically
              based on the new total population.
            </p>
          </details>
          <details class="faq-item">
            <summary>What happens if my payment fails?</summary>
            <p>
              We provide a 7-day grace period with up to 3 automatic retry attempts. We&rsquo;ll
              email you a notice and a link to update your payment method. If the grace period
              ends without a successful payment, your subscription is suspended and your market
              territory is returned to the public waitlist.
            </p>
          </details>
          <details class="faq-item">
            <summary>Is my market guaranteed forever?</summary>
            <p>
              No. Market exclusivity on Kiey is platform-only and conditional on continuous
              payment. It is not a lifetime grant and is not a real-estate territorial right.
              See Section 11 of the Terms of Service for the full text.
            </p>
          </details>
          <details class="faq-item">
            <summary>How is sales tax calculated?</summary>
            <p>
              Stripe Tax handles it automatically based on your billing address — U.S. state and
              local sales tax where Kiey has nexus, and Canadian GST/HST/PST/QST for Canadian
              billing addresses. The tax line is shown separately on your Stripe checkout page
              and on every renewal invoice; no action is required on your end.
            </p>
          </details>
        </section>

        <p class="subscribe-footer">
          Powered by Stripe. Your payment information is processed securely by Stripe and is
          never stored by Kiey.
        </p>
      </template>

      <!-- Spinner state — held while we wait for the Stripe Checkout URL
           and then for window.location to take over. -->
      <template v-else-if="!error_message">
        <div class="spinner" aria-hidden="true"></div>
        <h2 class="subscribe-title">Redirecting to secure checkout&hellip;</h2>
        <p class="subscribe-subtitle">
          Hold tight while we hand you off to Stripe to finish your subscription.
        </p>
      </template>

      <!-- Error state — recoverable. User can retry or contact support. -->
      <template v-else>
        <div class="error-icon" aria-hidden="true">!</div>
        <h2 class="subscribe-title">We couldn&rsquo;t open checkout</h2>
        <p class="subscribe-subtitle">{{ error_message }}</p>
        <div class="subscribe-actions">
          <button
            type="button"
            class="subscribe-btn subscribe-btn-primary"
            :disabled="retrying"
            @click="startCheckout"
          >
            {{ retrying ? 'Retrying&hellip;' : 'Try again' }}
          </button>
          <a
            class="subscribe-btn subscribe-btn-secondary"
            href="mailto:support@kiey.com?subject=Checkout%20issue"
          >
            Contact support
          </a>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import config from '@/config/constants.js';
import { renderLegalMarkdown } from '@/util/legal_md.js';
import { MARKET_PRICE_FLOOR } from '@/util/market_pricing';

// Versioning lets us prove later — for any specific tenant on any specific
// day — exactly which billing-terms text they accepted. Bump on any
// material edit to the Billing Terms section above. Server records this
// alongside billing_terms_accepted_at and billing_terms_accepted_ip.
const BILLING_TERMS_VERSION = '2026-05-03';

export default {
  name: 'BillingSubscribe',
  data() {
    return {
      // Mandatory acceptance gate — required for the Continue button to
      // enable. Defense in depth — server also rejects checkout if
      // billing_terms_accepted=true is not in the request body.
      terms_accepted: false,
      // Scroll-to-bottom gate. Defaults false; flips true when the user
      // scrolls the .terms-scrollbox to the end (within 12px of the
      // bottom). Until true, the acceptance checkbox is disabled. This
      // creates a stronger clickwrap record — the user provably saw the
      // entire ToS (incl. the No Reliance clause in §11) before assenting.
      terms_scrolled: false,
      // Held in the constant above; bound to the template so future bumps
      // are a one-line change.
      billing_terms_version: BILLING_TERMS_VERSION,
      // Spinner state — held while waiting for the Stripe Checkout URL.
      checkout_started: false,
      // Recoverable network/server failure — user can retry.
      error_message: '',
      retrying: false,
      // Coupon-code state. coupon_input is the raw text the user types;
      // coupon_applied_code is the value we forward to backend on
      // checkout. We never pre-validate — Stripe is the source of truth.
      coupon_open: false,
      coupon_input: '',
      coupon_applied_code: '',
      // Free Tier Partner Agreement state. Only surfaced when the
      // applied coupon code is a "free-tier" coupon (its uppercase form
      // contains "FREE"). Markdown is fetched once on mount from
      // /legal/free_tier_partner_agreement.md and rendered with the
      // shared legal_md util used by the standalone legal pages.
      free_tier_terms_html: '',
      // Full Terms of Service body — appended into the Billing Terms
      // scrollbox below the summary so users can scroll the full doc
      // without leaving the page or opening a new tab. Lazy-loaded once
      // on mount (cached afterward).
      full_tos_html: '',
      full_tos_loading: false,
      full_tos_error: '',
      full_tos_loaded: false,
      free_tier_terms_loading: false,
      free_tier_terms_error: '',
      free_tier_terms_loaded: false,
      free_tier_terms_accepted: false,
      free_terms_version: config.free_terms_current_version,
    };
  },
  computed: {
    ...mapState({
      my_market: state => state.Market_billing.my_market,
      billing_info: state => state.Market_billing.billing_info,
    }),
    // ZIP count comes from my_market (always present once signup_owner has
    // persisted the polygon). Falls through to billing_info if the live
    // listener hasn't run yet.
    zip_count() {
      if (this.my_market && Array.isArray(this.my_market.zip_codes)) {
        return this.my_market.zip_codes.length;
      }
      return 0;
    },
    // billing_info (from /v1/markets/billing) is the canonical source of
    // pricing — it's recomputed server-side on every claim/edit and
    // includes both the calculated and override prices. my_market only
    // carries the polygon, not the price.
    pricing_mode() {
      return (this.billing_info && this.billing_info.pricing_mode) || 'calculated';
    },
    has_override() {
      return this.pricing_mode === 'custom' &&
        this.billing_info &&
        typeof this.billing_info.monthly_price_override === 'number' &&
        this.billing_info.monthly_price_override > 0;
    },
    market_population() {
      // Backend caches the summed population on tenants/{id}.market_population.
      // billing_info doesn't expose that directly today; we'll derive it from
      // the calculated price (price * 1000 ≈ population, with $295 floor
      // ambiguity below 295k). For accuracy we prefer a direct field if
      // present; otherwise inverse-derive.
      if (!this.billing_info) return 0;
      const calc = this.billing_info.monthly_price_calculated;
      if (typeof calc === 'number' && calc > 0) {
        // calc = ceil(pop / 1000) so pop is in [(calc-1)*1000+1, calc*1000].
        // We can't recover the exact population without a dedicated field,
        // but the multiplication gives the upper bound which matches what
        // we'd display ("about 295,000 residents"). Acceptable for a
        // disclosure UI; precise population would need a backend field
        // exposed on billing_info.
        return calc * 1000;
      }
      return 0;
    },
    effective_price() {
      // Highest precedence: explicit override.
      if (this.has_override) {
        return this.billing_info.monthly_price_override;
      }
      // Calculated mode: use the floored monthly price as billed by Stripe.
      // billing_info.monthly_price_calculated is the *unfloored* per-1k
      // value, so apply the same floor the backend uses when charging.
      const calc = (this.billing_info && this.billing_info.monthly_price_calculated) || 0;
      if (calc > 0) {
        return Math.max(MARKET_PRICE_FLOOR, calc);
      }
      return 0;
    },
    formatted_price() {
      if (!this.effective_price || this.effective_price <= 0) return '';
      return Math.round(this.effective_price).toLocaleString('en-US');
    },
    // Friendly market label. We don't have city/state on my_market today, so
    // show "(N) ZIPs claimed" which still reads naturally above the count.
    market_label() {
      if (!this.zip_count) return '';
      return `${this.zip_count} ZIP${this.zip_count === 1 ? '' : 's'} claimed`;
    },
    // Has the user successfully attached a coupon to forward to checkout?
    coupon_applied() {
      return !!this.coupon_applied_code;
    },
    // True only when the applied coupon code is a "free-tier" coupon —
    // i.e. its uppercase form contains "FREE". This is the single client
    // signal that toggles the inline Free Tier Partner Agreement
    // section. Codes like KIEY_FREE_TEST, KIEY_FREE_PARTNER_2026,
    // FREEMONTH all match. KIEY50OFF, WELCOME50, etc. do not.
    show_free_tier_terms() {
      return this.coupon_applied && this.isFreeTierCoupon(this.coupon_input);
    },
    // Single source of truth for the Continue button. Disabled while a
    // request is in flight, until the billing-terms checkbox is on,
    // and — if a free-tier coupon was applied — until the partner-
    // agreement checkbox is also on.
    continue_disabled() {
      if (this.retrying) return true;
      if (!this.terms_scrolled) return true;
      if (!this.terms_accepted) return true;
      if (this.show_free_tier_terms && !this.free_tier_terms_accepted) return true;
      return false;
    },
  },
  mounted() {
    // Pull both the polygon (my_market) and the pricing snapshot
    // (billing_info) in parallel. Both are cached server-side; failures
    // here are swallowed so the page still renders with placeholders if
    // either endpoint is unreachable.
    this.$store.dispatch('Market_billing/fetch_markets').catch(() => {});
    this.$store.dispatch('Market_billing/fetch_billing_info').catch(() => {});
    // Lazy-load the full ToS into the Billing Terms scrollbox. Best-effort.
    this.loadFullTos();
  },
  methods: {
    confirmAndContinue() {
      // Defense in depth — button is disabled when continue_disabled is
      // true, but we also block here so a determined click via DevTools
      // can't skip the acceptance gates.
      if (this.continue_disabled) return;
      this.checkout_started = true;
      this.startCheckout();
    },
    // True for any coupon code whose uppercase form contains "FREE" —
    // matches KIEY_FREE_TEST, KIEY_FREE_PARTNER_2026, FREEMONTH, etc.
    // Does NOT match codes like KIEY50OFF or WELCOME50 that aren't
    // free-tier coupons even if they discount the price.
    isFreeTierCoupon(code) {
      return typeof code === 'string' && code.toUpperCase().includes('FREE');
    },
    // Scroll handler for the Billing Terms scrollbox. Flips terms_scrolled
    // to true once the user scrolls within 12px of the bottom (handles
    // sub-pixel rendering + zoom variance). Idempotent — once true, stays
    // true even if the user scrolls back up. Defensive: if the content
    // is short enough that there's no scroll bar at all, we treat that as
    // already-at-bottom.
    onTermsScroll(e) {
      if (this.terms_scrolled) return;
      const el = e && e.target;
      if (!el) return;
      const reached =
        el.scrollHeight - el.clientHeight <= 4 || // no scroll needed
        el.scrollTop + el.clientHeight >= el.scrollHeight - 12;
      if (reached) this.terms_scrolled = true;
    },
    // Re-check after the full ToS markdown finishes rendering — if the
    // page resized smaller than the scrollbox content there'd be no
    // scroll event. We wait a tick for the DOM to settle, then test.
    recheckTermsScrolled() {
      this.$nextTick(() => {
        const el = this.$refs && this.$refs.terms_scrollbox;
        if (!el) return;
        if (el.scrollHeight - el.clientHeight <= 4) {
          this.terms_scrolled = true;
        }
      });
    },
    // Lazy-load the full Terms of Service markdown into the bottom of
    // the Billing Terms scrollbox. Triggered once on mount; cached for
    // the lifetime of the page.
    loadFullTos() {
      if (this.full_tos_loaded || this.full_tos_loading) return;
      this.full_tos_loading = true;
      this.full_tos_error = '';
      fetch('/legal/terms_preview.md', { cache: 'no-store' })
        .then((res) => {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then((text) => {
          this.full_tos_html = renderLegalMarkdown(text);
          this.full_tos_loaded = true;
          // After the long ToS body renders, re-check whether the
          // scrollbox actually overflows. If it doesn't (rare — only on
          // unusually large viewports), there's no scroll event coming
          // and we'd leave the user stuck. Treat "no overflow" as
          // already-at-bottom.
          this.recheckTermsScrolled();
        })
        .catch((err) => {
          console.error('[billing] failed to load full terms of service:', err);
          this.full_tos_error =
            'Could not load the full Terms of Service. Please refresh the page or try again.';
        })
        .finally(() => {
          this.full_tos_loading = false;
        });
    },
    // Lazy-load the partner-agreement markdown the first time we have a
    // reason to show it (free-tier coupon applied). Cached so it doesn't
    // re-fetch / re-render on every keystroke or checkbox toggle.
    loadFreeTierTerms() {
      if (this.free_tier_terms_loaded || this.free_tier_terms_loading) return;
      this.free_tier_terms_loading = true;
      this.free_tier_terms_error = '';
      fetch('/legal/free_tier_partner_agreement.md', { cache: 'no-store' })
        .then((res) => {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then((text) => {
          this.free_tier_terms_html = renderLegalMarkdown(text);
          this.free_tier_terms_loaded = true;
        })
        .catch((err) => {
          console.error('[billing] failed to load free tier agreement:', err);
          this.free_tier_terms_error =
            'Could not load the Free Tier Partner Agreement. Please refresh the page or try again.';
        })
        .finally(() => {
          this.free_tier_terms_loading = false;
        });
    },
    // Optimistically attach the coupon to the local component state.
    // We do NOT round-trip to Stripe to validate — Stripe Checkout is
    // the only authoritative validator, and a network round-trip here
    // would only mask the same error message at a different time. If
    // the code is bogus, backend's create_checkout_session.js maps
    // Stripe's coupon_not_found / coupon_expired to a clean 400 +
    // INVALID_COUPON, which we surface in the error branch.
    applyCoupon() {
      const code = (this.coupon_input || '').trim();
      if (!code) return;
      this.coupon_applied_code = code;
      // Trigger markdown fetch when a free-tier code is applied so the
      // panel renders without a perceptible delay once the user reaches
      // it. Idempotent and cached.
      if (this.isFreeTierCoupon(code)) {
        this.loadFreeTierTerms();
      }
    },
    clearCoupon() {
      this.coupon_input = '';
      this.coupon_applied_code = '';
      // Clear acceptance + reset to closed state so an accidental
      // re-apply doesn't carry stale acceptance forward.
      this.free_tier_terms_accepted = false;
    },
    async startCheckout() {
      this.error_message = '';
      this.retrying = true;
      try {
        // signup_owner already persisted the polygon to markets/ for this
        // tenant — skip_claim=true tells the store to go straight to the
        // Checkout session (no second claim). billing_terms_accepted +
        // billing_terms_version flow through to the backend so it can
        // record acceptance on the tenant + user docs.
        // Only forward the partner-agreement acceptance when the panel
        // was visible AND the box was checked. The computed
        // show_free_tier_terms gate covers the "user applied + then
        // removed the coupon" case so we don't send stale acceptance.
        const free_tier_to_send =
          this.show_free_tier_terms && this.free_tier_terms_accepted;
        const result = await this.$store.dispatch('Market_billing/checkout', {
          skip_claim: true,
          billing_terms_accepted: true,
          billing_terms_version: this.billing_terms_version,
          // Optional — only attached when the user actually applied a
          // coupon code. Backend forwards this as
          // discounts: [{ coupon: ... }] to Stripe Checkout.
          coupon_code: this.coupon_applied_code || null,
          // Only attached when an applied free-tier coupon is on the
          // session AND the inline acceptance checkbox was ticked.
          free_terms_accepted: free_tier_to_send,
          free_terms_version: free_tier_to_send ? this.free_terms_version : null,
        });
        if (result && result.success && result.url) {
          window.location.href = result.url;
          return;
        }
        this.error_message =
          (result && result.message) ||
          "We couldn't reach our payment provider. Please try again or contact support.";
        // Reset checkout_started so the user lands back in the form, not
        // in the spinner state, after a recoverable failure.
        this.checkout_started = false;
      } catch (err) {
        console.error('[billing_checkout] dispatch failed:', err);
        this.error_message =
          "We couldn't reach our payment provider. Please try again or contact support.";
        this.checkout_started = false;
      } finally {
        this.retrying = false;
      }
    },
  },
};
</script>

<style scoped>
.subscribe-page {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 32px 16px 80px;
  box-sizing: border-box;
  background-color: #f5f7fb;
}

.subscribe-card {
  width: 100%;
  max-width: 720px;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 40px 40px 32px;
  box-shadow: 0 8px 24px rgba(6, 29, 71, 0.08);
  box-sizing: border-box;
}

/* Stepper (mirrors agent.vue + market_selection.vue) */
.signup-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 28px;
  max-width: 360px;
}
.signup-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 88px;
}
.signup-step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e4e8f1;
  /* Bumped from #9aa2b4 for WCAG AA contrast. */
  color: #5a6478;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}
.signup-step-active .signup-step-circle { background: #061d47; color: #fff; }
.signup-step-done .signup-step-circle { background: #19a99d; color: #fff; }
.signup-step-label {
  font-size: 11px;
  color: #5a6478;
  margin-top: 4px;
  letter-spacing: 0.3px;
  font-weight: 600;
  text-transform: uppercase;
}
.signup-step-active .signup-step-label { color: #061d47; }
.signup-step-done .signup-step-label { color: #19a99d; }
.signup-step-line {
  flex: 0 1 80px;
  height: 2px;
  background: #e4e8f1;
  margin: 0 6px 18px;
}
.signup-step-line-done { background: #19a99d; }

/* Header */
.subscribe-header {
  text-align: center;
  margin-bottom: 28px;
}
.subscribe-eyebrow {
  font-size: 11px;
  letter-spacing: 0.6px;
  color: #6a7388;
  text-transform: uppercase;
  font-weight: 600;
  margin: 0 0 6px;
}
.subscribe-title {
  color: #061d47;
  font-family: 'Roboto-Medium', Arial, sans-serif;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.25;
  margin: 0 0 10px;
}
.subscribe-subtitle {
  color: #4a4a4a;
  font-family: Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
  margin: 0;
}

/* Section blocks */
.subscribe-section {
  margin: 28px 0 0;
}
.section-title {
  color: #061d47;
  font-family: 'Roboto-Medium', Arial, sans-serif;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  margin: 0 0 6px;
}
/* Subtitle / caption directly under a section title — explanatory copy
   that's not part of a data row. Plain prose, slightly muted. */
.section-caption {
  color: #6a7388;
  font-size: 13.5px;
  line-height: 1.5;
  margin: 0 0 14px;
}

/* Market summary card */
.market-card {
  border: 1px solid #e4e8f1;
  border-radius: 10px;
  padding: 18px 20px;
  background: #fafbfd;
}
.market-card-row {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}
.market-card-cell {
  flex: 1 1 140px;
  min-width: 120px;
}
.market-card-label {
  color: #6a7388;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.market-card-value {
  color: #061d47;
  font-size: 16px;
  font-weight: 500;
}
.muted { color: #9aa2b4; }

/* Pricing card */
.pricing-card {
  border: 1px solid #e4e8f1;
  border-radius: 10px;
  padding: 18px 20px;
  background: #ffffff;
}
.pricing-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  font-size: 14px;
  line-height: 1.45;
}
.pricing-label {
  color: #2d2d2d;
  flex: 1 1 240px;
}
.pricing-value {
  color: #061d47;
  font-weight: 600;
  white-space: nowrap;
}
.pricing-formula {
  color: #5a6478;
  font-weight: 500;
  font-size: 13px;
  white-space: normal;
  text-align: right;
}
.pricing-row-emphasis {
  font-size: 16px;
}
.pricing-row-emphasis .pricing-value {
  color: #061d47;
  font-size: 18px;
}
.pricing-row-tax .pricing-tax-note {
  color: #6a7388;
  font-weight: 500;
  font-size: 13px;
}
.pricing-row-override .pricing-value {
  color: #061d47;
  font-size: 18px;
}
.pricing-divider {
  height: 1px;
  background: #e4e8f1;
  margin: 8px 0 4px;
}
.pricing-row-total {
  font-weight: 700;
}
.pricing-row-total .pricing-label {
  color: #061d47;
  font-weight: 700;
}
.pricing-note {
  color: #5a6478;
  font-size: 13px;
  margin: 4px 0 8px;
  font-style: italic;
}

/* Terms scrollbox — fixed-height with overflow-y so the user has to
   physically encounter the terms before they can accept them. tabindex
   lets keyboard users scroll the box with arrow keys. */
.terms-scrollbox {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #c7d3f0;
  border-radius: 10px;
  padding: 18px 20px;
  background: #fafbfd;
  font-size: 13px;
  line-height: 1.55;
  color: #2d2d2d;
}
/* Full-height variant for the Free Tier Partner Agreement —
   we want users to actually read the whole thing, not skim a
   scrollable window. */
.terms-scrollbox-full {
  max-height: none;
  overflow-y: visible;
}
.terms-scrollbox:focus-visible {
  outline: 2px solid #29abe2;
  outline-offset: 2px;
}
/* End-of-document marker inside the scrollbox. Subtle "— End of Terms —"
   line that doubles as the trip-wire users cross to enable acceptance. */
.terms-end-marker {
  text-align: center;
  margin: 18px 0 4px;
  color: #8e96a8;
  font-size: 11px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  font-weight: 600;
}

/* "Please scroll to the end" hint above the disabled checkbox. Disappears
   once the user reaches the end of the scrollbox. Generous vertical
   breathing room above and below so the hint reads as its own line of
   guidance rather than crammed between the scrollbox and the checkbox
   card. */
.terms-scroll-hint {
  margin: 22px 4px 18px 4px;
  color: #6a7388;
  font-size: 12.5px;
  font-style: italic;
}

/* Visual cue that the checkbox is currently disabled (waiting for scroll
   completion). Reduces opacity rather than fully greying out so the user
   can still see it's the assent control. */
.terms-consent-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.terms-consent-disabled input[type="checkbox"] {
  cursor: not-allowed;
}

/* Visual divider between the Billing Terms summary and the full ToS body
   inside the same scrollbox. */
.terms-divider {
  border: none;
  border-top: 1px solid #d6deef;
  margin: 22px 0 18px;
}
/* Full ToS rendered HTML — slightly tighter line-height and smaller base
   font than the summary list so the long doc reads cleaner inside the
   scrollbox. Prose styles cascade from the rendered markdown. */
.terms-fulltos {
  font-size: 12.5px;
  line-height: 1.55;
  color: #2d2d2d;
}
.terms-fulltos h1, .terms-fulltos h2, .terms-fulltos h3 {
  color: #061d47;
  margin-top: 18px;
  margin-bottom: 8px;
}
.terms-fulltos h1 { font-size: 15px; font-weight: 700; }
.terms-fulltos h2 { font-size: 13.5px; font-weight: 700; }
.terms-fulltos h3 { font-size: 12.5px; font-weight: 700; }
.terms-fulltos p { margin: 8px 0; }
.terms-fulltos ul, .terms-fulltos ol { margin: 6px 0 10px 20px; padding: 0; }
.terms-fulltos li { margin: 3px 0; }
.terms-fulltos strong { color: #061d47; }
.terms-fulltos table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 12px; }
.terms-fulltos th, .terms-fulltos td { border: 1px solid #d6deef; padding: 6px 8px; text-align: left; }
.terms-fulltos blockquote { margin: 8px 0; padding: 6px 12px; border-left: 3px solid #29abe2; background: #f0f6fb; }
.terms-fulltos a { color: #29abe2; }
.terms-heading {
  color: #061d47;
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 12px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.terms-version {
  color: #6a7388;
  font-size: 11px;
  font-weight: 500;
  font-family: monospace;
}
.terms-list {
  margin: 0 0 12px;
  padding: 0 0 0 18px;
}
.terms-list li {
  margin-bottom: 10px;
}
.terms-list strong {
  color: #061d47;
}
.terms-footer {
  margin: 12px 0 0;
  padding-top: 10px;
  border-top: 1px solid #e4e8f1;
  color: #5a6478;
  font-size: 12px;
}
.terms-footer a {
  color: #29abe2;
  text-decoration: none;
}
.terms-footer a:hover { text-decoration: underline; }

/* Free Tier Partner Agreement body — rendered inside the same
   .terms-scrollbox so it inherits the bordered + scrollable card.
   Tightened type scale so the in-card heading hierarchy doesn't blow
   out the visual weight of the section above. */
.free-terms-body {
  color: #2d2d2d;
  line-height: 1.6;
  font-size: 13px;
}
.free-terms-body >>> h2 {
  color: #061d47;
  font-size: 15px;
  font-weight: 700;
  margin: 18px 0 8px;
}
.free-terms-body >>> h2:first-child { margin-top: 0; }
.free-terms-body >>> h3 {
  color: #061d47;
  font-size: 13px;
  font-weight: 700;
  margin: 14px 0 6px;
}
.free-terms-body >>> p { margin: 0 0 10px; }
.free-terms-body >>> ul,
.free-terms-body >>> ol { margin: 6px 0 12px; padding-left: 20px; }
.free-terms-body >>> li { margin-bottom: 4px; }
.free-terms-body >>> strong { color: #061d47; font-weight: 700; }
.free-terms-body >>> a { color: #29abe2; text-decoration: none; }
.free-terms-body >>> a:hover { text-decoration: underline; }
.free-terms-body >>> hr {
  border: 0;
  border-top: 1px solid #e4e8f1;
  margin: 18px 0;
}
.free-terms-body >>> code {
  background: #eef0f5;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 0.9em;
  font-family: 'SF Mono', Menlo, Monaco, Consolas, monospace;
}

/* Acceptance checkbox */
.terms-consent {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 22px;
  padding: 18px 20px;
  border: 1px solid #c7d3f0;
  border-radius: 8px;
  background: #f4f7ff;
  font-size: 14px;
  line-height: 1.5;
  color: #061d47;
  cursor: pointer;
}
.terms-consent input[type='checkbox'] {
  margin-top: 2px;
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  cursor: pointer;
}

/* Coupon code (optional) — collapsed by default. The toggle reads as a
   plain text link so it doesn't compete with Continue for visual weight. */
.coupon-section {
  margin-top: 28px;
}
.coupon-toggle {
  background: transparent;
  border: none;
  padding: 6px 0;
  color: #29abe2;
  font-size: 14px;
  font-weight: 600;
  font-family: Arial, sans-serif;
  cursor: pointer;
}
.coupon-toggle:hover {
  text-decoration: underline;
}
.coupon-toggle:focus-visible {
  outline: 2px solid #29abe2;
  outline-offset: 2px;
  border-radius: 4px;
}
.coupon-panel {
  /* Plain block — no card-in-card-in-card. Inherits the section's
     left/right alignment so it lines up with the rest of the page. */
  padding: 0;
  margin-top: 4px;
}
.coupon-label {
  display: block;
  color: #6a7388;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
}
.coupon-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.coupon-input {
  flex: 1 1 200px;
  min-width: 160px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #c7d3f0;
  border-radius: 8px;
  background: #fff;
  color: #061d47;
  font-size: 14px;
  font-family: Arial, sans-serif;
  box-sizing: border-box;
}
.coupon-input:disabled {
  background: #eef0f5;
  color: #6a7388;
}
.coupon-input:focus {
  outline: none;
  border-color: #29abe2;
  box-shadow: 0 0 0 2px rgba(41, 171, 226, 0.2);
}
.coupon-btn {
  height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  border: 1px solid #061d47;
  background: #061d47;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  font-family: Arial, sans-serif;
  cursor: pointer;
}
.coupon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.coupon-btn:hover:not(:disabled) {
  background: #0a2a66;
}
.coupon-btn-clear {
  background: #fff;
  color: #061d47;
}
.coupon-btn-clear:hover {
  background: #061d47;
  color: #fff;
}
.coupon-note {
  color: #5a6478;
  font-size: 12px;
  margin: 8px 0 0;
}
.coupon-note-ok {
  color: #19a99d;
  font-weight: 600;
}

/* Continue button */
.subscribe-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 28px;
}
.subscribe-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 32px;
  border-radius: 24px;
  font-family: Arial, sans-serif;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid transparent;
  box-sizing: border-box;
  min-width: 240px;
}
.subscribe-btn-primary {
  background: #29abe2;
  color: #ffffff;
}
.subscribe-btn-primary:hover:not(:disabled) {
  background: #1f9bd0;
}
.subscribe-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.subscribe-btn-secondary {
  background: #ffffff;
  color: #061d47;
  border-color: #061d47;
}
.subscribe-btn-secondary:hover {
  background: #061d47;
  color: #ffffff;
}

/* FAQ — native <details>/<summary> for free a11y. */
.faq-section {
  margin-top: 36px;
}
.faq-item {
  border-top: 1px solid #e4e8f1;
  padding: 12px 0;
}
.faq-item:last-of-type {
  border-bottom: 1px solid #e4e8f1;
}
.faq-item summary {
  cursor: pointer;
  list-style: none;
  color: #061d47;
  font-size: 15px;
  font-weight: 600;
  padding: 6px 0;
  position: relative;
  padding-right: 28px;
}
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item summary::after {
  content: '+';
  position: absolute;
  right: 4px;
  top: 4px;
  font-size: 20px;
  font-weight: 400;
  color: #29abe2;
  line-height: 1;
}
.faq-item[open] summary::after {
  content: '\2212'; /* minus */
}
.faq-item summary:focus-visible {
  outline: 2px solid #29abe2;
  outline-offset: 2px;
  border-radius: 4px;
}
.faq-item p {
  color: #4a4a4a;
  font-size: 14px;
  line-height: 1.55;
  margin: 8px 0 4px;
}
.faq-item a {
  color: #29abe2;
  text-decoration: none;
}
.faq-item a:hover { text-decoration: underline; }

/* Footer */
.subscribe-footer {
  text-align: center;
  color: #6a7388;
  font-size: 12px;
  line-height: 1.5;
  margin: 32px 0 0;
}

/* Spinner + error states (re-used from old layout) */
.spinner {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 4px solid #e4e8f1;
  border-top-color: #29abe2;
  margin: 16px auto 24px;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.error-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fdecec;
  color: #c0392b;
  font-size: 32px;
  font-weight: 700;
  margin: 16px auto 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Responsive */
@media (max-width: 720px) {
  .subscribe-card {
    padding: 28px 20px 24px;
  }
  .signup-stepper { max-width: 280px; }
  .signup-step { min-width: 76px; }
  .signup-step-line { flex: 0 1 60px; }
  .signup-step-label { font-size: 10px; }
  .subscribe-title { font-size: 22px; }
  .pricing-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  .pricing-row .pricing-value { white-space: normal; }
  .pricing-formula { text-align: left; }
  .market-card-row { gap: 16px; }
  .market-card-cell { flex: 1 1 45%; }
  .subscribe-btn {
    width: 100%;
    min-width: 0;
  }
}
</style>
